import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, FileCheck2, Loader2, Lock, Pencil, Plus, Send, Trash2 } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/efs/PageHeader';
import { ListPagination } from '@/components/efs/ListPagination';
import { EVIDENCE_MEDIA_ACCEPT } from '@/features/evidence/api';
import { useAttachEvidenceFromLibrary, useUploadEvidence } from '@/features/evidence/useEvidence';
import {
  EVIDENCE_MEDIA_MIME_GROUPS,
  FileLibraryPickerDialog,
  FileSourceChooser,
  fileMatchesEvidenceMediaAccept,
} from '@/features/file-library';
import type { FileLibrarySelection, FileSourceMode } from '@/features/file-library/types';
import { useFileLibraryFeatureEnabled } from '@/features/file-library/useFileLibraryFeature';
import { useDestinationsList } from '@/features/masters/destinations/useDestinations';
import { useWarehousesList } from '@/features/masters/warehouses/useWarehouses';
import { useCurrentUser } from '@/lib/auth-context';
import type { ApiClientError } from '@/lib/api-client';
import { formatDate, formatDateTime, formatKg } from '@/lib/format';
import { hasPermission } from '@/lib/permissions';

import { OutboundDraftDialog } from './OutboundDraftDialog';
import {
  useAttachReceiptEvidence,
  useAttachResourceChainEvidence,
  useConfirmOutboundReceipt,
  useConfirmOutboundResourceChain,
  useConfirmOutboundSalesResult,
  useDispatchOutbound,
  useLockOutbound,
  useOutbounds,
  useVoidOutbound,
  useWaiveOutboundReceipt,
  useWaiveOutboundResourceChain,
} from './useOutbounds';
import type {
  ConfirmOutboundSalesResultInput,
  OutboundDto,
  OutboundHistoryFilter,
  OutboundReceiptStatus,
  OutboundStatus,
} from './types';
import { SalesResultDialog } from './SalesResultDialog';

const P = 'efs.sales.outbounds';
const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100] as const;

const FILTERS: OutboundHistoryFilter[] = [
  'all',
  'buyer',
  'secondary_processor',
  'draft',
  'dispatched',
  'awaiting_receipt',
  'completed',
];

const statusVariants: Record<OutboundStatus, 'default' | 'secondary' | 'outline' | 'destructive'> =
  {
    draft: 'secondary',
    dispatched: 'default',
    sales_result_confirmed: 'default',
    locked: 'outline',
    voided: 'destructive',
  };

const receiptVariants: Record<OutboundReceiptStatus, 'default' | 'secondary' | 'outline'> = {
  pending: 'secondary',
  uploaded: 'secondary',
  confirmed: 'default',
  waived: 'outline',
};

const isReceiptFinished = (status: OutboundReceiptStatus | null) =>
  status === 'confirmed' || status === 'waived';

const matchesFilter = (row: OutboundDto, filter: OutboundHistoryFilter): boolean => {
  switch (filter) {
    case 'all':
      return true;
    case 'buyer':
    case 'secondary_processor':
      return row.destination_type_snapshot === filter;
    case 'draft':
      return row.outbound_status === 'draft';
    case 'dispatched':
      return row.outbound_status === 'dispatched';
    case 'awaiting_receipt':
      return row.receipt_status === 'pending' || row.receipt_status === 'uploaded';
    case 'completed':
      return row.outbound_status === 'locked' || row.outbound_status === 'sales_result_confirmed';
  }
};

interface VoidDialogProps {
  outbound: OutboundDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onVoid: (reason: string) => Promise<void>;
  pending: boolean;
}

const VoidDialog = ({ outbound, open, onOpenChange, onVoid, pending }: VoidDialogProps) => {
  const { t } = useTranslation('sales');
  const [reason, setReason] = useState('');

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setReason('');
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(`${P}.void.title`, { defaultValue: '出荷草稿を無効化' })}</DialogTitle>
          <DialogDescription>
            {t(`${P}.void.subtitle`, {
              defaultValue: `${outbound?.outbound_code ?? ''} の予約を解放し、履歴を保持します。`,
              code: outbound?.outbound_code ?? '',
            })}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={t(`${P}.void.reason`, { defaultValue: '無効化理由' })}
          rows={3}
        />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            {t('efs.common.actions.cancel', { ns: 'common', defaultValue: 'キャンセル' })}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={!reason.trim() || pending}
            onClick={async () => {
              await onVoid(reason.trim());
              handleOpenChange(false);
            }}
          >
            {pending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {t(`${P}.actions.void`, { defaultValue: '無効化' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

type WaiverKind = 'receipt' | 'resource_chain';

interface WaiverTarget {
  outbound: OutboundDto;
  kind: WaiverKind;
}

type EvidenceUploadKind = 'dispatch' | 'receipt' | 'resource_chain';

interface LibraryAttachTarget {
  outbound: OutboundDto;
  kind: EvidenceUploadKind;
}

interface PendingLibraryAttach extends LibraryAttachTarget {
  file: FileLibrarySelection;
}

interface WaiverDialogProps {
  target: WaiverTarget | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onWaive: (kind: WaiverKind, reason: string) => Promise<void>;
  pending: boolean;
}

const WaiverDialog = ({ target, open, onOpenChange, onWaive, pending }: WaiverDialogProps) => {
  const { t } = useTranslation('sales');
  const [reason, setReason] = useState('');

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) setReason('');
    onOpenChange(nextOpen);
  };

  const titleKey =
    target?.kind === 'resource_chain' ? `${P}.waiver.resource_title` : `${P}.waiver.receipt_title`;
  const titleDefault = target?.kind === 'resource_chain' ? '証明免除理由' : '回収免除理由';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(titleKey, { defaultValue: titleDefault })}</DialogTitle>
          <DialogDescription>
            {t(`${P}.waiver.subtitle`, {
              defaultValue: `${target?.outbound.outbound_code ?? ''} を免除する理由を記録します。`,
              code: target?.outbound.outbound_code ?? '',
            })}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          aria-label={t(`${P}.waiver.reason`, { defaultValue: '免除理由' })}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={t(`${P}.waiver.reason`, { defaultValue: '免除理由' })}
          rows={3}
        />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            {t('efs.common.actions.cancel', { ns: 'common', defaultValue: 'キャンセル' })}
          </Button>
          <Button
            type="button"
            disabled={!reason.trim() || pending}
            onClick={async () => {
              if (!target) return;
              await onWaive(target.kind, reason.trim());
              handleOpenChange(false);
            }}
          >
            {pending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {t(`${P}.actions.waive`, { defaultValue: '免除する' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const OutboundsPage = () => {
  const { t } = useTranslation('sales');
  const { batchId } = useParams<{ batchId: string }>();
  const user = useCurrentUser();

  const [filter, setFilter] = useState<OutboundHistoryFilter>('all');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [draftOpen, setDraftOpen] = useState(false);
  const [draftTarget, setDraftTarget] = useState<OutboundDto | null>(null);
  const [voidTarget, setVoidTarget] = useState<OutboundDto | null>(null);
  const [waiverTarget, setWaiverTarget] = useState<WaiverTarget | null>(null);
  const [libraryTarget, setLibraryTarget] = useState<LibraryAttachTarget | null>(null);
  const [pendingLibraryAttach, setPendingLibraryAttach] = useState<PendingLibraryAttach | null>(
    null,
  );
  const [salesResultTarget, setSalesResultTarget] = useState<OutboundDto | null>(null);

  useEffect(() => {
    setPage(1);
  }, [batchId]);

  const listFilters = useMemo(
    () => ({
      history_filter: filter,
      page,
      per_page: perPage,
    }),
    [filter, page, perPage],
  );

  const query = useOutbounds(batchId, listFilters);
  const destinationsQuery = useDestinationsList({ status: 'approved', per_page: 500 });
  const warehousesQuery = useWarehousesList({ active_only: true, per_page: 500 });
  const uploadEvidence = useUploadEvidence();
  const attachEvidenceFromLibrary = useAttachEvidenceFromLibrary();
  const fileLibraryEnabled = useFileLibraryFeatureEnabled();
  const dispatch = useDispatchOutbound();
  const receiptEvidence = useAttachReceiptEvidence();
  const confirmReceipt = useConfirmOutboundReceipt();
  const waiveReceipt = useWaiveOutboundReceipt();
  const resourceChainEvidence = useAttachResourceChainEvidence();
  const confirmResourceChain = useConfirmOutboundResourceChain();
  const waiveResourceChain = useWaiveOutboundResourceChain();
  const salesResult = useConfirmOutboundSalesResult();
  const lock = useLockOutbound();
  const voidMutation = useVoidOutbound();

  const canDraft = hasPermission(user, 'shipment.manage');
  const canDispatch = hasPermission(user, 'shipment.manage');
  const canReceipt = hasPermission(user, 'shipment.manage');
  const canWaiveEvidence = hasPermission(user, 'mass_balance.manage');
  const canResourceEvidence = hasPermission(user, 'secondary_process.manage');
  const canConfirmResource = hasPermission(user, 'processing.material_reentry.confirm');
  const canSalesResult = hasPermission(user, 'sales.actual_result.confirm');
  const canLock = hasPermission(user, 'sales.lock');
  const canUseFileLibrary = fileLibraryEnabled && hasPermission(user, 'file_library.read');

  const destinationNames = useMemo(
    () =>
      new Map(
        (destinationsQuery.data?.rows ?? []).map((destination) => [
          destination.destination_company_id,
          destination.company_display_name,
        ]),
      ),
    [destinationsQuery.data?.rows],
  );
  const destinationCompanyIds = useMemo(
    () =>
      new Map(
        (destinationsQuery.data?.rows ?? []).map((destination) => [
          destination.destination_company_id,
          destination.company_id,
        ]),
      ),
    [destinationsQuery.data?.rows],
  );
  const warehouseNames = useMemo(
    () =>
      new Map(
        (warehousesQuery.data?.rows ?? []).map((warehouse) => [
          warehouse.warehouse_id,
          `${warehouse.code} · ${warehouse.display_name}`,
        ]),
      ),
    [warehousesQuery.data?.rows],
  );

  const rows = useMemo(
    () => (query.data?.data ?? []).filter((row) => matchesFilter(row, filter)),
    [filter, query.data?.data],
  );
  const total = query.data?.meta?.total ?? 0;

  const changeFilter = (value: OutboundHistoryFilter) => {
    setFilter(value);
    setPage(1);
  };

  const changePerPage = (nextPerPage: number) => {
    setPerPage(nextPerPage === 100 ? 100 : DEFAULT_PER_PAGE);
    setPage(1);
  };

  const mutations = [
    dispatch,
    receiptEvidence,
    confirmReceipt,
    waiveReceipt,
    resourceChainEvidence,
    confirmResourceChain,
    waiveResourceChain,
    salesResult,
    lock,
    voidMutation,
  ];
  const actionError =
    uploadEvidence.error ??
    attachEvidenceFromLibrary.error ??
    (mutations.map((mutation) => mutation.error).find(Boolean) as ApiClientError | null) ??
    null;
  const anyPending =
    uploadEvidence.isPending ||
    attachEvidenceFromLibrary.isPending ||
    mutations.some((mutation) => mutation.isPending);

  const actionForKind = (kind: EvidenceUploadKind) => {
    if (kind === 'dispatch') return dispatch;
    if (kind === 'receipt') return receiptEvidence;
    return resourceChainEvidence;
  };

  const uploadAndRun = async (
    outbound: OutboundDto,
    file: File,
    action: {
      mutateAsync: (variables: { id: string; evidenceFileId: string }) => Promise<unknown>;
    },
  ) => {
    if (!batchId) return;
    const evidence = await uploadEvidence.mutateAsync({
      batchId,
      file,
      evidence_type: 'photo',
    });
    await action.mutateAsync({
      id: outbound.outbound_id,
      evidenceFileId: evidence.evidence_file_id,
    });
  };

  const attachLibraryAndRun = async (target: PendingLibraryAttach) => {
    if (!batchId) return;
    setPendingLibraryAttach(target);
    const evidence = await attachEvidenceFromLibrary.mutateAsync({
      batchId,
      companyFileId: target.file.company_file_id,
      evidenceType: 'photo',
    });
    await actionForKind(target.kind).mutateAsync({
      id: target.outbound.outbound_id,
      evidenceFileId: evidence.evidence_file_id,
    });
    setPendingLibraryAttach(null);
  };

  const openLibraryPicker = (outbound: OutboundDto, kind: EvidenceUploadKind) => {
    attachEvidenceFromLibrary.reset();
    setLibraryTarget({ outbound, kind });
  };

  const openCreate = () => {
    setDraftTarget(null);
    setDraftOpen(true);
  };

  const openEdit = (outbound: OutboundDto) => {
    setDraftTarget(outbound);
    setDraftOpen(true);
  };

  const openSalesResult = (outbound: OutboundDto) => {
    salesResult.reset();
    setSalesResultTarget(outbound);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={t(`${P}.title`, { defaultValue: '出荷・販売' })}
        subtitle={t(`${P}.subtitle`, {
          defaultValue: '一般買方と二次処理業者への販売を、一つの出荷履歴で管理します。',
        })}
        count={total}
        actions={
          canDraft && batchId ? (
            <Button onClick={openCreate}>
              <Plus className="mr-1 h-4 w-4" />
              {t(`${P}.actions.create`, { defaultValue: '草稿を作成' })}
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap gap-2" role="group" aria-label={t(`${P}.filters.label`)}>
        {FILTERS.map((value) => (
          <Button
            key={value}
            type="button"
            size="sm"
            variant={filter === value ? 'default' : 'outline'}
            onClick={() => changeFilter(value)}
          >
            {t(`${P}.filters.${value}`, { defaultValue: value })}
          </Button>
        ))}
      </div>

      {actionError ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t(`${P}.error.title`, { defaultValue: '操作できませんでした' })}</AlertTitle>
          <AlertDescription>{actionError.message}</AlertDescription>
          {pendingLibraryAttach ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-3"
              disabled={anyPending}
              onClick={() => void attachLibraryAndRun(pendingLibraryAttach)}
            >
              {t('efs.file_library.picker.retry', {
                ns: 'fileLibrary',
                defaultValue: '選択したファイルで再試行',
              })}
            </Button>
          ) : null}
        </Alert>
      ) : null}

      {query.isLoading ? (
        <div className="flex items-center gap-2 py-12 text-sm text-efs-text-muted">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('efs.common.actions.loading', { ns: 'common', defaultValue: '読み込み中…' })}
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-efs-border py-12 text-center text-sm text-efs-text-muted">
          {t(`${P}.empty`, { defaultValue: '該当する出荷はありません。' })}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-efs-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t(`${P}.fields.outbound_code`, { defaultValue: '出荷コード' })}
                </TableHead>
                <TableHead>
                  {t(`${P}.fields.planned_dispatch_date`, { defaultValue: '出荷予定日' })}
                </TableHead>
                <TableHead>{t(`${P}.fields.destination`, { defaultValue: '出荷先' })}</TableHead>
                <TableHead>{t(`${P}.fields.lines`, { defaultValue: '出荷明細' })}</TableHead>
                <TableHead>{t(`${P}.fields.status`, { defaultValue: '状態' })}</TableHead>
                <TableHead>
                  {t(`${P}.fields.receipt_status`, { defaultValue: '回収書類' })}
                </TableHead>
                <TableHead>
                  {t(`${P}.fields.resource_chain_status`, { defaultValue: '資源循環証明' })}
                </TableHead>
                <TableHead className="text-right">
                  {t(`${P}.fields.actions`, { defaultValue: '操作' })}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <OutboundRow
                  key={`${row.legacy_source ?? 'canonical'}:${row.outbound_id}`}
                  row={row}
                  destinationName={destinationNames.get(row.destination_company_id)}
                  warehouseNames={warehouseNames}
                  canDraft={canDraft}
                  canDispatch={canDispatch}
                  canReceipt={canReceipt}
                  canWaiveEvidence={canWaiveEvidence}
                  canResourceEvidence={canResourceEvidence}
                  canConfirmResource={canConfirmResource}
                  canSalesResult={canSalesResult}
                  canLock={canLock}
                  canUseFileLibrary={canUseFileLibrary}
                  pending={anyPending}
                  onEdit={() => openEdit(row)}
                  onVoid={() => setVoidTarget(row)}
                  onDispatch={(file) => uploadAndRun(row, file, dispatch)}
                  onDispatchFromLibrary={() => openLibraryPicker(row, 'dispatch')}
                  onReceiptEvidence={(file) => uploadAndRun(row, file, receiptEvidence)}
                  onReceiptEvidenceFromLibrary={() => openLibraryPicker(row, 'receipt')}
                  onConfirmReceipt={() => confirmReceipt.mutate(row.outbound_id)}
                  onWaiveReceipt={() => setWaiverTarget({ outbound: row, kind: 'receipt' })}
                  onResourceChainEvidence={(file) => uploadAndRun(row, file, resourceChainEvidence)}
                  onResourceChainEvidenceFromLibrary={() =>
                    openLibraryPicker(row, 'resource_chain')
                  }
                  onConfirmResourceChain={() => confirmResourceChain.mutate(row.outbound_id)}
                  onWaiveResourceChain={() =>
                    setWaiverTarget({ outbound: row, kind: 'resource_chain' })
                  }
                  onSalesResult={() => openSalesResult(row)}
                  onLock={() => lock.mutate(row.outbound_id)}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ListPagination
        page={page}
        perPage={perPage}
        total={total}
        pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
        onPageChange={setPage}
        onPerPageChange={changePerPage}
      />

      {batchId ? (
        <OutboundDraftDialog
          batchId={batchId}
          open={draftOpen}
          outbound={draftTarget}
          onOpenChange={(open) => {
            setDraftOpen(open);
            if (!open) setDraftTarget(null);
          }}
        />
      ) : null}

      <VoidDialog
        outbound={voidTarget}
        open={!!voidTarget}
        pending={voidMutation.isPending}
        onOpenChange={(open) => {
          if (!open) setVoidTarget(null);
        }}
        onVoid={async (reason) => {
          if (!voidTarget) return;
          await voidMutation.mutateAsync({ id: voidTarget.outbound_id, reason });
        }}
      />

      <WaiverDialog
        target={waiverTarget}
        open={!!waiverTarget}
        pending={waiveReceipt.isPending || waiveResourceChain.isPending}
        onOpenChange={(open) => {
          if (!open) setWaiverTarget(null);
        }}
        onWaive={async (kind, reason) => {
          if (!waiverTarget) return;
          const variables = { id: waiverTarget.outbound.outbound_id, reason };
          if (kind === 'resource_chain') {
            await waiveResourceChain.mutateAsync(variables);
          } else {
            await waiveReceipt.mutateAsync(variables);
          }
        }}
      />

      <SalesResultDialog
        outbound={salesResultTarget}
        open={!!salesResultTarget}
        pending={salesResult.isPending}
        errorMessage={salesResult.error?.message}
        destinationCompanyId={
          salesResultTarget
            ? destinationCompanyIds.get(salesResultTarget.destination_company_id)
            : undefined
        }
        onOpenChange={(open) => {
          if (!open) setSalesResultTarget(null);
        }}
        onConfirm={async (body: ConfirmOutboundSalesResultInput) => {
          if (!salesResultTarget) return;
          await salesResult.mutateAsync({
            id: salesResultTarget.outbound_id,
            body,
          });
        }}
      />

      <FileLibraryPickerDialog
        open={!!libraryTarget}
        onOpenChange={(open) => {
          if (!open) setLibraryTarget(null);
        }}
        batchId={batchId}
        acceptMimeGroups={EVIDENCE_MEDIA_MIME_GROUPS}
        acceptFile={fileMatchesEvidenceMediaAccept}
        onSelect={(file) => {
          if (!libraryTarget) return;
          void attachLibraryAndRun({ ...libraryTarget, file });
        }}
      />
    </div>
  );
};

interface OutboundRowProps {
  row: OutboundDto;
  destinationName?: string;
  warehouseNames: Map<string, string>;
  canDraft: boolean;
  canDispatch: boolean;
  canReceipt: boolean;
  canWaiveEvidence: boolean;
  canResourceEvidence: boolean;
  canConfirmResource: boolean;
  canSalesResult: boolean;
  canLock: boolean;
  canUseFileLibrary: boolean;
  pending: boolean;
  onEdit: () => void;
  onVoid: () => void;
  onDispatch: (file: File) => void;
  onDispatchFromLibrary: () => void;
  onReceiptEvidence: (file: File) => void;
  onReceiptEvidenceFromLibrary: () => void;
  onConfirmReceipt: () => void;
  onWaiveReceipt: () => void;
  onResourceChainEvidence: (file: File) => void;
  onResourceChainEvidenceFromLibrary: () => void;
  onConfirmResourceChain: () => void;
  onWaiveResourceChain: () => void;
  onSalesResult: () => void;
  onLock: () => void;
}

const OutboundRow = ({
  row,
  destinationName,
  warehouseNames,
  canDraft,
  canDispatch,
  canReceipt,
  canWaiveEvidence,
  canResourceEvidence,
  canConfirmResource,
  canSalesResult,
  canLock,
  canUseFileLibrary,
  pending,
  onEdit,
  onVoid,
  onDispatch,
  onDispatchFromLibrary,
  onReceiptEvidence,
  onReceiptEvidenceFromLibrary,
  onConfirmReceipt,
  onWaiveReceipt,
  onResourceChainEvidence,
  onResourceChainEvidenceFromLibrary,
  onConfirmResourceChain,
  onWaiveResourceChain,
  onSalesResult,
  onLock,
}: OutboundRowProps) => {
  const { t } = useTranslation('sales');
  const dispatchInput = useRef<HTMLInputElement>(null);
  const receiptInput = useRef<HTMLInputElement>(null);
  const resourceInput = useRef<HTMLInputElement>(null);
  const [dispatchSource, setDispatchSource] = useState<FileSourceMode>('local');
  const [receiptSource, setReceiptSource] = useState<FileSourceMode>('local');
  const [resourceSource, setResourceSource] = useState<FileSourceMode>('local');
  const code = row.outbound_code ?? row.outbound_id.slice(0, 8);
  const readOnly = !!row.legacy_source;
  const receiptFinished = isReceiptFinished(row.receipt_status);
  const resourceFinished =
    row.destination_type_snapshot === 'buyer' || isReceiptFinished(row.resource_chain_status);
  const canLockNow =
    row.outbound_status === 'sales_result_confirmed' && receiptFinished && resourceFinished;

  const pickFile = (event: React.ChangeEvent<HTMLInputElement>, callback: (file: File) => void) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) callback(file);
  };

  return (
    <TableRow>
      <TableCell>
        <div className="font-mono text-xs">{code}</div>
        {row.legacy_source ? (
          <Badge variant="outline" className="mt-1">
            {t(`${P}.legacy.${row.legacy_source}`, { defaultValue: row.legacy_source })}
          </Badge>
        ) : null}
      </TableCell>
      <TableCell>
        {formatDate(row.planned_dispatch_date)}
        {row.dispatched_at ? (
          <div className="text-[0.6875rem] text-efs-text-muted">
            {formatDateTime(row.dispatched_at)}
          </div>
        ) : null}
      </TableCell>
      <TableCell>
        <div>{destinationName ?? row.destination_company_id}</div>
        <div className="text-xs text-efs-text-muted">
          {t(`${P}.destination_type.${row.destination_type_snapshot}`, {
            defaultValue: row.destination_type_snapshot,
          })}
        </div>
      </TableCell>
      <TableCell>
        <div className="space-y-1">
          {row.lines.map((line) => (
            <div key={line.outbound_line_id} className="text-xs">
              <span>{line.item_name_snapshot}</span>
              <span className="ml-1 font-mono">{formatKg(line.kg)} kg</span>
              <span className="ml-1 text-efs-text-muted">
                {warehouseNames.get(line.warehouse_id) ?? line.warehouse_id}
              </span>
            </div>
          ))}
        </div>
      </TableCell>
      <TableCell>
        <Badge variant={statusVariants[row.outbound_status]}>
          {t(`${P}.status.${row.outbound_status}`, { defaultValue: row.outbound_status })}
        </Badge>
      </TableCell>
      <TableCell>
        {row.receipt_status ? (
          <Badge variant={receiptVariants[row.receipt_status]}>
            {t(`${P}.receipt_status.${row.receipt_status}`, {
              defaultValue: row.receipt_status,
            })}
          </Badge>
        ) : (
          <span aria-label={t(`${P}.receipt_status.not_started`, { defaultValue: '未出荷' })}>
            —
          </span>
        )}
      </TableCell>
      <TableCell>
        {row.destination_type_snapshot === 'buyer' ? (
          <span className="text-xs text-efs-text-muted">
            {t(`${P}.resource_chain_status.not_applicable`, { defaultValue: '対象外' })}
          </span>
        ) : row.resource_chain_status ? (
          <Badge variant={receiptVariants[row.resource_chain_status]}>
            {t(`${P}.resource_chain_status.${row.resource_chain_status}`, {
              defaultValue: row.resource_chain_status,
            })}
          </Badge>
        ) : (
          <span>—</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex min-w-52 flex-wrap justify-end gap-1.5">
          {!readOnly && row.outbound_status === 'draft' && canDraft ? (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-label={`${code} を編集`}
                onClick={onEdit}
                disabled={pending}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-label={`${code} を無効化`}
                onClick={onVoid}
                disabled={pending}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          ) : null}

          {!readOnly && row.outbound_status === 'draft' && canDispatch ? (
            <>
              <input
                ref={dispatchInput}
                type="file"
                accept={EVIDENCE_MEDIA_ACCEPT}
                className="hidden"
                onChange={(event) => pickFile(event, onDispatch)}
              />
              {canUseFileLibrary ? (
                <FileSourceChooser
                  value={dispatchSource}
                  onChange={setDispatchSource}
                  disabled={pending}
                />
              ) : null}
              <Button
                type="button"
                size="sm"
                onClick={() =>
                  dispatchSource === 'library'
                    ? onDispatchFromLibrary()
                    : dispatchInput.current?.click()
                }
                disabled={pending}
              >
                <Send className="mr-1 h-3.5 w-3.5" />
                {t(`${P}.actions.dispatch`, { defaultValue: '出荷確定' })}
              </Button>
            </>
          ) : null}

          {!readOnly &&
          (canReceipt || canWaiveEvidence) &&
          (row.outbound_status === 'dispatched' ||
            row.outbound_status === 'sales_result_confirmed') &&
          (row.receipt_status === 'pending' || row.receipt_status === 'uploaded') ? (
            <>
              {row.receipt_status === 'pending' && canReceipt ? (
                <>
                  <input
                    ref={receiptInput}
                    type="file"
                    accept={EVIDENCE_MEDIA_ACCEPT}
                    className="hidden"
                    onChange={(event) => pickFile(event, onReceiptEvidence)}
                  />
                  {canUseFileLibrary ? (
                    <FileSourceChooser
                      value={receiptSource}
                      onChange={setReceiptSource}
                      disabled={pending}
                    />
                  ) : null}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      receiptSource === 'library'
                        ? onReceiptEvidenceFromLibrary()
                        : receiptInput.current?.click()
                    }
                    disabled={pending}
                  >
                    {t(`${P}.actions.receipt_evidence`, { defaultValue: '回収書類を追加' })}
                  </Button>
                </>
              ) : row.receipt_status === 'uploaded' && canReceipt ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={onConfirmReceipt}
                  disabled={pending}
                >
                  <FileCheck2 className="mr-1 h-3.5 w-3.5" />
                  {t(`${P}.actions.confirm_receipt`, { defaultValue: '回収を確認' })}
                </Button>
              ) : null}
              {canWaiveEvidence ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={onWaiveReceipt}
                  disabled={pending}
                >
                  {t(`${P}.actions.waive_receipt`, { defaultValue: '回収免除' })}
                </Button>
              ) : null}
            </>
          ) : null}

          {!readOnly &&
          (canResourceEvidence || canConfirmResource || canWaiveEvidence) &&
          row.destination_type_snapshot === 'secondary_processor' &&
          (row.outbound_status === 'dispatched' ||
            row.outbound_status === 'sales_result_confirmed') &&
          (row.resource_chain_status === 'pending' || row.resource_chain_status === 'uploaded') ? (
            <>
              {row.resource_chain_status === 'pending' && canResourceEvidence ? (
                <>
                  <input
                    ref={resourceInput}
                    type="file"
                    accept={EVIDENCE_MEDIA_ACCEPT}
                    className="hidden"
                    onChange={(event) => pickFile(event, onResourceChainEvidence)}
                  />
                  {canUseFileLibrary ? (
                    <FileSourceChooser
                      value={resourceSource}
                      onChange={setResourceSource}
                      disabled={pending}
                    />
                  ) : null}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      resourceSource === 'library'
                        ? onResourceChainEvidenceFromLibrary()
                        : resourceInput.current?.click()
                    }
                    disabled={pending}
                  >
                    {t(`${P}.actions.resource_chain_evidence`, {
                      defaultValue: '資源循環証明を追加',
                    })}
                  </Button>
                </>
              ) : row.resource_chain_status === 'uploaded' && canConfirmResource ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={onConfirmResourceChain}
                  disabled={pending}
                >
                  {t(`${P}.actions.confirm_resource_chain`, {
                    defaultValue: '資源循環を確認',
                  })}
                </Button>
              ) : null}
              {canWaiveEvidence ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={onWaiveResourceChain}
                  disabled={pending}
                >
                  {t(`${P}.actions.waive_resource_chain`, { defaultValue: '証明免除' })}
                </Button>
              ) : null}
            </>
          ) : null}

          {!readOnly && canSalesResult && row.outbound_status === 'dispatched' ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onSalesResult}
              disabled={pending}
            >
              {t(`${P}.actions.sales_result_confirm`, { defaultValue: '販売実績確定' })}
            </Button>
          ) : null}

          {!readOnly && canLock && row.outbound_status === 'sales_result_confirmed' ? (
            <Button type="button" size="sm" onClick={onLock} disabled={pending || !canLockNow}>
              <Lock className="mr-1 h-3.5 w-3.5" />
              {t(`${P}.actions.lock`, { defaultValue: 'ロック' })}
            </Button>
          ) : null}
        </div>
      </TableCell>
    </TableRow>
  );
};

export default OutboundsPage;
